import { useEffect, useRef, useState, type ReactNode } from 'react';
import { ArrowDown, Loader2 } from 'lucide-react';
import { useIsDesktop } from '@/lib/use-media-query';
import { cn } from '@/lib/utils';

const THRESHOLD = 60;
const RESIST = 0.45;

/**
 * Lightweight pull-to-refresh wrapper for read-only surfaces. Active
 * only on `< md` viewports and only when the page is scrolled to the
 * top. No new dependency.
 *
 * Strategy: track touchstart Y when scrollTop is 0; accumulate
 * downward delta with a 0.45 resistance factor so the gesture feels
 * weighty (not rubber-bandy). Past 60px the indicator's copy flips
 * from "Pull to refresh" → "Release to refresh". On release we call
 * `onRefresh()` and clamp the indicator's state until it resolves.
 *
 * Touch events only — desktop is a no-op (the `<main>` element
 * already gets a normal scroll-up affordance).
 */
export default function PullToRefresh({
  children,
  onRefresh,
}: {
  children: ReactNode;
  onRefresh: () => Promise<unknown>;
}) {
  const isDesktop = useIsDesktop();
  const containerRef = useRef<HTMLDivElement>(null);
  const startY = useRef<number | null>(null);
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (isDesktop) return;
    const node = containerRef.current;
    if (!node) return;

    const onTouchStart = (e: TouchEvent) => {
      if (window.scrollY > 0) return;
      const t = e.touches[0];
      if (!t) return;
      startY.current = t.pageY;
    };

    const onTouchMove = (e: TouchEvent) => {
      if (startY.current === null) return;
      const t = e.touches[0];
      if (!t) return;
      const delta = t.pageY - startY.current;
      if (delta <= 0) {
        setPull(0);
        return;
      }
      // Resistance + clamp.
      setPull(Math.min(delta * RESIST, THRESHOLD * 1.5));
    };

    const onTouchEnd = async () => {
      const distance = pull;
      startY.current = null;
      if (distance >= THRESHOLD && !refreshing) {
        setRefreshing(true);
        setPull(THRESHOLD);
        try {
          await onRefresh();
        } finally {
          setRefreshing(false);
          setPull(0);
        }
      } else {
        setPull(0);
      }
    };

    node.addEventListener('touchstart', onTouchStart, { passive: true });
    node.addEventListener('touchmove', onTouchMove, { passive: true });
    node.addEventListener('touchend', onTouchEnd);
    node.addEventListener('touchcancel', onTouchEnd);
    return () => {
      node.removeEventListener('touchstart', onTouchStart);
      node.removeEventListener('touchmove', onTouchMove);
      node.removeEventListener('touchend', onTouchEnd);
      node.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [isDesktop, onRefresh, pull, refreshing]);

  const reached = pull >= THRESHOLD;

  return (
    <div ref={containerRef} className="relative">
      {/* Indicator — sits in the gap created by the translate of
          children. Hides under the page when pull is 0. */}
      <div
        aria-hidden
        style={{ height: pull }}
        className={cn(
          'overflow-hidden flex items-end justify-center transition-[height] duration-100',
          isDesktop && 'hidden',
        )}
      >
        <div
          className={cn(
            'flex items-center gap-2 pb-2 text-xs font-mono uppercase tracking-wider',
            reached ? 'text-primary' : 'text-muted-foreground',
          )}
        >
          {refreshing ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <ArrowDown className={cn('h-4 w-4 transition-transform', reached && 'rotate-180')} />
          )}
          {refreshing ? 'Refreshing' : reached ? 'Release to refresh' : 'Pull to refresh'}
        </div>
      </div>
      <div
        style={{ transform: pull ? `translateY(${pull}px)` : undefined }}
        className="transition-transform duration-100"
      >
        {children}
      </div>
    </div>
  );
}
